import { CsvSection } from "../csvExport";
import { formatPercent } from "../formatters";

export interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

export interface CategoryCount {
  label: string;
  value: number;
}

export interface StepBarSection {
  kind: "bar";
  title: string;
  allTitle: string;
  leadTitle: string;
  qualTitle: string;
  allBars: CategoryCount[];
  leadBars: CategoryCount[];
  qualBars: CategoryCount[];
}

export interface StepDonutSection {
  kind: "donut";
  title: string;
  leadTitle: string;
  qualTitle: string;
  leadTotal: number;
  qualTotal: number;
  leadSegments: DonutSegment[];
  qualSegments: DonutSegment[];
}

export interface FunnelStep {
  label: string;
  count: number;
}

// The live usersMainFunnelId query returns one row per step, but in
// arbitrary order - this is the canonical funnel order the UI renders in.
// "step" values are matched exactly as the query returns them.
const FUNNEL_STEP_ORDER = [
  "click_in",
  "Funding amount",
  "Purpose",
  "Time in business",
  "Monthly revenue",
  "Business bank account",
  "Industry",
  "Personal credit",
  "Contact details",
  "Zip code",
  "Qualified",
];

// Maps the live usersMainFunnelId query's `query_output` array (see
// OptifundDashboardEndPointLogic.runQuery, which unwraps query_output
// before this runs) into the funnel's canonical step order, defaulting any
// step missing from the response to 0 rather than dropping it.
export function mapMainFunnelApiRows(raw: any[]): FunnelStep[] {
  const countByStep = new Map(raw.map((row) => [row?.step, row?.users ?? 0]));
  return FUNNEL_STEP_ORDER.map((step) => ({
    label: step,
    count: countByStep.get(step) ?? 0,
  }));
}

// Both usersLeadToQualifiedId and usersQualifiedByZipId return the same
// `{ zip_code_reported: "YES" | "NO", users }[]` shape (the field is named
// zip_code_reported on both, even for the lead-to-qualified query).
function usersByYesNo(raw: any[]): { yes: number; no: number } {
  const usersByAnswer = new Map(raw.map((row) => [row?.zip_code_reported, row?.users ?? 0]));
  return { yes: usersByAnswer.get("YES") ?? 0, no: usersByAnswer.get("NO") ?? 0 };
}

export function mapLeadToQualifiedSegments(raw: any[]): DonutSegment[] {
  const { yes, no } = usersByYesNo(raw);
  return [
    { label: "NO", value: no, color: "#8E24AA" },
    { label: "YES", value: yes, color: "#7CB342" },
  ];
}

export function mapQualifiedByZipSegments(raw: any[]): DonutSegment[] {
  const { yes, no } = usersByYesNo(raw);
  return [
    { label: "NO", value: no, color: "#B39DDB" },
    { label: "YES", value: yes, color: "#7E57C2" },
  ];
}

function formatDollarLabel(amount: number): string {
  return `$${amount.toLocaleString("en-US")}`;
}

function usersByNumericValue(raw: any[], valueField: string): Map<number, number> {
  return new Map(raw.map((row) => [row?.[valueField], row?.users ?? 0]));
}

// Shared by borrow_amount (Step 1) and monthly_revenue (Step 4) - both are
// numeric dollar-figure categories with no free-text pollution. The "all"
// query's amounts are the category superset - lead/qualified are confirmed
// subsets of it (missing a category entirely rather than reporting 0) - so
// all three bars are built against the "all" category list, defaulting any
// amount missing from lead/qualified's own response to 0. This keeps all
// three charts in a step section on identical x-axis categories.
function mapDollarCategoryStepSection(
  allRaw: any[],
  leadRaw: any[],
  qualifiedRaw: any[],
  valueField: string,
  title: string,
  fieldSlug: string
): StepBarSection {
  const amounts = allRaw
    .map((row) => row?.[valueField])
    .filter((amount): amount is number => typeof amount === "number")
    .sort((a, b) => a - b);

  const allByAmount = usersByNumericValue(allRaw, valueField);
  const leadByAmount = usersByNumericValue(leadRaw, valueField);
  const qualifiedByAmount = usersByNumericValue(qualifiedRaw, valueField);

  const toBars = (byAmount: Map<number, number>): CategoryCount[] =>
    amounts.map((amount) => ({ label: formatDollarLabel(amount), value: byAmount.get(amount) ?? 0 }));

  return {
    kind: "bar",
    title,
    allTitle: fieldSlug,
    leadTitle: `${fieldSlug}_is_lead`,
    qualTitle: `${fieldSlug}_is_qualified`,
    allBars: toBars(allByAmount),
    leadBars: toBars(leadByAmount),
    qualBars: toBars(qualifiedByAmount),
  };
}

export function mapBorrowAmountStepSection(
  allRaw: any[],
  leadRaw: any[],
  qualifiedRaw: any[]
): StepBarSection {
  return mapDollarCategoryStepSection(
    allRaw,
    leadRaw,
    qualifiedRaw,
    "borrow_amount",
    "Step 1 - Borrow Amount",
    "borrow"
  );
}

export function mapMonthlyRevenueStepSection(
  allRaw: any[],
  leadRaw: any[],
  qualifiedRaw: any[]
): StepBarSection {
  return mapDollarCategoryStepSection(
    allRaw,
    leadRaw,
    qualifiedRaw,
    "monthly_revenue",
    "Step 4 - Monthly Revenue",
    "rev"
  );
}

// Some step questions have a free-text "Other, please specify" field -
// e.g. loan_purpose returns dozens of one-off custom answers (~1 user
// each) alongside a handful of real dropdown options. Rendering every
// distinct raw value would produce an unreadable chart, so anything not in
// the known category list below is folded into "other" (summing with any
// literal "other" answers). This is a fixed whitelist rather than a
// count-based heuristic, so the same raw value always buckets the same
// way regardless of which date range is selected.
function bucketCategoryCounts(raw: any[], valueField: string, categories: string[]): CategoryCount[] {
  const countByCategory = new Map<string, number>(categories.map((category) => [category, 0]));
  for (const row of raw) {
    const value = row?.[valueField];
    const users = row?.users ?? 0;
    const category = countByCategory.has(value) ? value : "other";
    countByCategory.set(category, (countByCategory.get(category) ?? 0) + users);
  }
  return categories.map((category) => ({ label: category, value: countByCategory.get(category) ?? 0 }));
}

// cashflow/expansion/vehicle/equipment/inventory/refinance/payroll/
// marketing/taxes are the mockup's original loan-purpose categories;
// realestate showed up in the live data and wasn't in that original list.
const LOAN_PURPOSE_CATEGORIES = [
  "cashflow",
  "expansion",
  "vehicle",
  "equipment",
  "inventory",
  "refinance",
  "payroll",
  "marketing",
  "taxes",
  "realestate",
  "other",
];

export function mapLoanPurposeStepSection(
  allRaw: any[],
  leadRaw: any[],
  qualifiedRaw: any[]
): StepBarSection {
  return {
    kind: "bar",
    title: "Step 2 - Loan Purpose",
    allTitle: "purpose",
    leadTitle: "purpose_is_lead",
    qualTitle: "purpose_is_qualified",
    allBars: bucketCategoryCounts(allRaw, "loan_purpose", LOAN_PURPOSE_CATEGORIES),
    leadBars: bucketCategoryCounts(leadRaw, "loan_purpose", LOAN_PURPOSE_CATEGORIES),
    qualBars: bucketCategoryCounts(qualifiedRaw, "loan_purpose", LOAN_PURPOSE_CATEGORIES),
  };
}

// For closed-enum fields (a fixed dropdown, no free-text option) - unlike
// bucketCategoryCounts above, nothing gets folded into "other" here since
// there isn't one; any category simply defaults to 0 if a response omits it.
function usersByCategory(raw: any[], valueField: string): Map<string, number> {
  return new Map(raw.map((row) => [row?.[valueField], row?.users ?? 0]));
}

function toCategoryBars(byCategory: Map<string, number>, categories: string[]): CategoryCount[] {
  return categories.map((category) => ({ label: category, value: byCategory.get(category) ?? 0 }));
}

// Time in business is a closed 4-option enum (confirmed by the live
// response containing exactly these 4 values, in this order, matching the
// query's own ORDER BY case).
const TIME_IN_BUSINESS_CATEGORIES = ["0-6", "6-12", "12-24", "24+"];

export function mapTimeInBusinessStepSection(
  allRaw: any[],
  leadRaw: any[],
  qualifiedRaw: any[]
): StepBarSection {
  return {
    kind: "bar",
    title: "Step 3 - Time in Business",
    allTitle: "tib",
    leadTitle: "tib_is_lead",
    qualTitle: "tib_is_qualified",
    allBars: toCategoryBars(usersByCategory(allRaw, "time_in_business"), TIME_IN_BUSINESS_CATEGORIES),
    leadBars: toCategoryBars(usersByCategory(leadRaw, "time_in_business"), TIME_IN_BUSINESS_CATEGORIES),
    qualBars: toCategoryBars(
      usersByCategory(qualifiedRaw, "time_in_business"),
      TIME_IN_BUSINESS_CATEGORIES
    ),
  };
}

// Step 5 has no "all" query (per the spec's query ID table) - just lead
// and qualified, each `{ business_banking: "yes" | "no", users }[]`.
function usersByYesNoLowercase(raw: any[], valueField: string): { yes: number; no: number } {
  const usersByAnswer = new Map(raw.map((row) => [row?.[valueField], row?.users ?? 0]));
  return { yes: usersByAnswer.get("yes") ?? 0, no: usersByAnswer.get("no") ?? 0 };
}

// Retail through Lawn care are the mockup's original industry categories;
// Real Estate showed up in the live data (Title Case, matching the other
// categories' convention) and wasn't in that original list. The live data
// also has a one-off "Econmerce" entry (count 1) - looks like a typo in
// the dropdown option itself rather than free text, but it's not aliased
// to "E-commerce" here (that would be guessing at a fix), so it folds into
// "other" like any other unrecognized value.
const BUSINESS_INDUSTRY_CATEGORIES = [
  "Retail",
  "Trucking",
  "Construction",
  "Beauty & Wellness",
  "Administrative",
  "Home services",
  "Healthcare",
  "Restaurant",
  "Housing",
  "Photography",
  "IT/networking",
  "Alcohol",
  "Pet care",
  "Finance",
  "E-commerce",
  "Courier",
  "Bakery",
  "Lawn care",
  "Real Estate",
  "other",
];

export function mapBusinessIndustryStepSection(
  allRaw: any[],
  leadRaw: any[],
  qualifiedRaw: any[]
): StepBarSection {
  return {
    kind: "bar",
    title: "Step 6 - Business Industry",
    allTitle: "industry",
    leadTitle: "industry_is_lead",
    qualTitle: "industry_is_qualified",
    allBars: bucketCategoryCounts(allRaw, "business_industry", BUSINESS_INDUSTRY_CATEGORIES),
    leadBars: bucketCategoryCounts(leadRaw, "business_industry", BUSINESS_INDUSTRY_CATEGORIES),
    qualBars: bucketCategoryCounts(qualifiedRaw, "business_industry", BUSINESS_INDUSTRY_CATEGORIES),
  };
}

// Closed 6-option enum driven by the query's own ORDER BY case (579 or
// below/580-619/620-689/690-719/720 or above, then "I'm not sure" falls to
// the case's `else 99` and sorts last). "720 or above" didn't appear in the
// sample response but is a valid case branch, so it's kept as a category
// defaulting to 0 rather than dropped. Dashes in the 3 middle labels are
// en-dashes ("–"), matching the API's own field values exactly.
const CREDIT_RATING_CATEGORIES = [
  "579 or below",
  "580 – 619",
  "620 – 689",
  "690 – 719",
  "720 or above",
  "I'm not sure",
];

export function mapPersonalCreditStepSection(
  allRaw: any[],
  leadRaw: any[],
  qualifiedRaw: any[]
): StepBarSection {
  return {
    kind: "bar",
    title: "Step 7 - Personal Credit",
    allTitle: "credit",
    leadTitle: "credit_is_lead",
    qualTitle: "credit_is_qualified",
    allBars: toCategoryBars(usersByCategory(allRaw, "credit_rating"), CREDIT_RATING_CATEGORIES),
    leadBars: toCategoryBars(usersByCategory(leadRaw, "credit_rating"), CREDIT_RATING_CATEGORIES),
    qualBars: toCategoryBars(
      usersByCategory(qualifiedRaw, "credit_rating"),
      CREDIT_RATING_CATEGORIES
    ),
  };
}

// Zero-value segments are dropped, matching how UsersDonutChart itself
// renders (see its own zero-filtering) - the exported CSV should reflect
// exactly what's visible on screen.
export function toMainFunnelCsvSection(steps: FunnelStep[], total: number): CsvSection {
  return {
    title: "Main Funnel",
    headers: ["Step", "Users", "% of total"],
    rows: steps.map((step) => [
      step.label,
      step.count,
      formatPercent(total ? (step.count / total) * 100 : 0, 2),
    ]),
  };
}

export function toDonutSegmentsCsvSection(title: string, segments: DonutSegment[]): CsvSection {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const visibleSegments = segments.filter((segment) => segment.value > 0);
  return {
    title,
    headers: ["Segment", "Users", "% of total"],
    rows: visibleSegments.map((segment) => [
      segment.label,
      segment.value,
      formatPercent(total ? (segment.value / total) * 100 : 0, 1),
    ]),
  };
}

// allBars/leadBars/qualBars share identical category order by construction
// (every mapXStepSection function above builds all three from the same
// category list), so they combine into one category-per-row table rather
// than three separate ones.
export function toBarSectionCsvSection(section: StepBarSection): CsvSection {
  return {
    title: section.title,
    headers: ["Category", "All", "Lead", "Qualified"],
    rows: section.allBars.map((bar, index) => [
      bar.label,
      bar.value,
      section.leadBars[index]?.value ?? 0,
      section.qualBars[index]?.value ?? 0,
    ]),
  };
}

// leadSegments/qualSegments share identical segment order by construction
// (mapBusinessBankingStepSection builds both from the same [yes, no] list),
// so they combine into one segment-per-row table, same convention as the
// bar sections above.
export function toDonutSectionCsvSection(section: StepDonutSection): CsvSection {
  return {
    title: section.title,
    headers: ["Segment", "Lead", "Qualified"],
    rows: section.leadSegments.map((segment, index) => [
      segment.label,
      segment.value,
      section.qualSegments[index]?.value ?? 0,
    ]),
  };
}

export function mapBusinessBankingStepSection(leadRaw: any[], qualifiedRaw: any[]): StepDonutSection {
  const lead = usersByYesNoLowercase(leadRaw, "business_banking");
  const qualified = usersByYesNoLowercase(qualifiedRaw, "business_banking");

  return {
    kind: "donut",
    title: "Step 5 - Business Bank Account",
    leadTitle: "business_banking_is_lead",
    qualTitle: "business_banking_is_qualified",
    leadTotal: lead.yes + lead.no,
    qualTotal: qualified.yes + qualified.no,
    leadSegments: [
      { label: "yes", value: lead.yes, color: "#7E57C2" },
      { label: "no", value: lead.no, color: "#7CB342" },
    ],
    qualSegments: [
      { label: "yes", value: qualified.yes, color: "#7E57C2" },
      { label: "no", value: qualified.no, color: "#7CB342" },
    ],
  };
}
